import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token")}`,
});

function BrowseNews() {
  const [posts, setPosts] = useState([]);
  const [fetchingErr, setFetchingErr] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setFetchingErr(null);
    try {
      const res = await fetch(`${BASE_URL}/api/news/getposts?limit=100`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to fetch News posts");
      setPosts(data.data.items);
    } catch (error) {
      setFetchingErr(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this News post?")) return;
    try {
      const res = await fetch(`${BASE_URL}/api/news/deletepost/${postId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to delete");
      setPosts(posts.filter((post) => post._id !== postId));
      alert("Post Deleted");
    } catch (error) {
      alert(`Failed to Delete: ${error.message}`);
    }
  };

  const handleTogglePublish = async (post) => {
    const isPublished = post.status === "PUBLISHED";
    const action = isPublished ? "unpublish" : "publish";
    if (!window.confirm(`Are you sure you want to ${action} this News post?`)) return;
    setActionLoadingId(post._id);
    try {
      const res = await fetch(`${BASE_URL}/api/news/${action}/${post._id}`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Failed to ${action}`);
      setPosts(posts.map((p) => (p._id === post._id ? data.data : p)));
    } catch (error) {
      alert(`Failed to ${action}: ${error.message}`);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSearch = () => {
    if (!posts) return [];
    return posts.filter((post) => post.title.toLowerCase().includes(searchValue.toLowerCase()));
  };

  const filteredPosts = handleSearch();

  const renderCard = (post) => (
    <div className="col-md-4 d-flex align-items-stretch" key={post._id}>
      <div className="card h-100 shadow-sm border-0">
        <div className="position-relative">
          <img
            src={post.thumbnailImage || post.featuredImage}
            alt={post.title}
            className="card-img-top"
            style={{ height: "200px", objectFit: "cover" }}
          />
          <span
            className={`badge position-absolute top-0 start-0 m-2 ${post.status === "PUBLISHED" ? "bg-success" : "bg-secondary"}`}
            style={{ fontSize: "0.85rem", padding: "0.5em 1em", opacity: 0.95 }}
          >
            {post.status}
          </span>
        </div>
        <div className="card-body d-flex flex-column">
          <h5 className="card-title mb-2">{post.title}</h5>
          <p className="text-muted small mb-2" style={{ minHeight: "40px" }}>{post.shortDescription}</p>
          <div className="d-flex justify-content-between align-items-center gap-2 mt-auto">
            <button
              className={`btn btn-sm ${post.status === "PUBLISHED" ? "btn-outline-warning" : "btn-outline-success"}`}
              onClick={() => handleTogglePublish(post)}
              disabled={actionLoadingId === post._id}
            >
              {actionLoadingId === post._id ? "..." : post.status === "PUBLISHED" ? "Unpublish" : "Publish"}
            </button>
            <div className="d-flex gap-2">
              <Link to={`/news/${post._id}`}>
                <button className="btn btn-outline-primary btn-sm" title="Edit">
                  <i className="bi bi-pencil-square"></i>
                </button>
              </Link>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleDelete(post._id)}
                title="Delete"
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col-md-6 mx-auto">
          <input
            type="text"
            className="form-control shadow-sm"
            placeholder="Search News & Events by Title..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{
              borderRadius: "6px",
              borderColor: "#d0d5dd",
              padding: "0.65rem 0.85rem",
              fontSize: "16px",
              boxShadow: "0 2px 8px rgba(37,99,235,0.07)",
            }}
          />
        </div>
      </div>
      <div className="row g-4 justify-content-center">
        {loading ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : fetchingErr ? (
          <div className="col-12 text-danger text-center py-5">{fetchingErr}</div>
        ) : filteredPosts.length > 0 ? (
          filteredPosts.map(renderCard)
        ) : (
          <div className="col-12 text-center mt-5">
            {searchValue ? (
              <div className="alert alert-warning">Oops! No posts found.</div>
            ) : (
              <div className="alert alert-info">No News & Events yet, please create one.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BrowseNews;
